/**
 * PrimitiveSelector Component
 * 
 * Modal for selecting a primitive from the library.
 * Used in flow editor when configuring a Primitive Call node.
 */

import React, { useState, useEffect } from 'react';
import { FlowDefinition } from '@simcheck/shared-types';
import primitiveService from '../../services/primitiveService';
import SchemaPreview from '../primitives/SchemaPreview';

interface PrimitiveSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (primitive: FlowDefinition) => void;
  selectedPrimitiveId?: string;
}

export const PrimitiveSelector: React.FC<PrimitiveSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedPrimitiveId,
}) => {
  const [primitives, setPrimitives] = useState<FlowDefinition[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewingPrimitive, setViewingPrimitive] = useState<FlowDefinition | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPrimitives();
    }
  }, [isOpen]);

  const loadPrimitives = async () => {
    setLoading(true);
    setError(null);

    try {
      const [primitivesData, categoriesData] = await Promise.all([
        primitiveService.getPublishedPrimitives(),
        primitiveService.getCategories(),
      ]);

      setPrimitives(primitivesData);
      setCategories(categoriesData);
    } catch (err: any) {
      console.error('Failed to load primitives:', err);
      setError(err.message || 'Failed to load primitives');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrimitives = primitives.filter((primitive) => {
    const matchesSearch =
      primitive.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (primitive.description && primitive.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || primitive.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSelect = (primitive: FlowDefinition) => {
    onSelect(primitive);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-border dark:border-border-subtle">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">
                Select Primitive
              </h2>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-sm text-status-error mb-4">{error}</p>
                <button
                  onClick={loadPrimitives}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {/* Search & Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search primitives..."
                      className="block w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                  </div>
                  {categories.length > 0 && (
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Primitives List */}
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredPrimitives.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-sm text-muted-foreground">
                        No primitives found
                      </p>
                    </div>
                  ) : (
                    filteredPrimitives.map((primitive) => (
                      <div
                        key={primitive.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          selectedPrimitiveId === primitive.id
                            ? 'border-accent-blue bg-accent-blue/10 dark:bg-accent-blue/20'
                            : 'border-border hover:border-accent-blue/50 hover:bg-muted'
                        }`}
                        onClick={() => handleSelect(primitive)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">
                                {primitive.name}
                              </h4>
                              {primitive.category && (
                                <span className="text-xs px-2 py-0.5 bg-accent-blue/10 dark:bg-accent-blue/20 text-accent-blue rounded">
                                  {primitive.category}
                                </span>
                              )}
                            </div>
                            {primitive.description && typeof primitive.description === 'string' && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {primitive.description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground/70">
                              {primitive.inputSchema?.properties && (
                                <span>
                                  {Object.keys(primitive.inputSchema.properties).length} input(s)
                                </span>
                              )}
                              {primitive.outputSchema?.properties && (
                                <span>
                                  {Object.keys(primitive.outputSchema.properties).length} output(s)
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingPrimitive(primitive);
                            }}
                            className="p-2 text-muted-foreground hover:text-accent-blue hover:bg-muted rounded"
                            title="View Details"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-muted border-t border-border dark:border-border-subtle flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted border border-border rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {viewingPrimitive && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setViewingPrimitive(null)}
            ></div>

            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="px-6 py-4 border-b border-border dark:border-border-subtle flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {viewingPrimitive.name}
                  </h2>
                  {viewingPrimitive.description && typeof viewingPrimitive.description === 'string' && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {viewingPrimitive.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setViewingPrimitive(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-4 space-y-4 max-h-96 overflow-y-auto">
                <SchemaPreview
                  schema={viewingPrimitive.inputSchema}
                  title="Input Schema"
                  emptyMessage="No inputs required"
                />
                <SchemaPreview
                  schema={viewingPrimitive.outputSchema}
                  title="Output Schema"
                  emptyMessage="No outputs returned"
                />
              </div>

              <div className="px-6 py-4 bg-muted border-t border-border dark:border-border-subtle flex items-center justify-between gap-3">
                <button
                  onClick={() => setViewingPrimitive(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted border border-border rounded transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleSelect(viewingPrimitive);
                    setViewingPrimitive(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-blue hover:bg-accent-blue/90 rounded transition-colors"
                >
                  Select This Primitive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrimitiveSelector;


